import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import type { FieldBaseProps } from './types';

type Props<T extends FieldValues> = FieldBaseProps<T> & {
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
};

export function FormNumberField<T extends FieldValues>({
  name,
  label,
  description,
  required,
  disabled,
  className,
  placeholder,
  min,
  max,
  step,
  unit,
}: Props<T>) {
  const { control } = useFormContext<T>();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label ? (
            <FormLabel>
              {label}
              {required ? <span aria-hidden="true"> *</span> : null}
            </FormLabel>
          ) : null}
          <FormControl>
            <div className="relative">
              <Input
                type="number"
                inputMode="decimal"
                placeholder={placeholder}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === '' ? undefined : Number(v));
                }}
              />
              {unit ? (
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                  {unit}
                </span>
              ) : null}
            </div>
          </FormControl>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
